import { Automation } from "@/types/v2/Automation";
import { Label } from "@/components/Base/Label";
import { useState } from "react";
import { Input } from "@/components/Base/Input";
import { AutomationDisplay } from "@/components/Automations/AutomationDisplay";
import { Button } from "@/components/Base/Button";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DownloadIcon,
  PencilIcon,
  SaveIcon,
  TrashIcon,
} from "lucide-react";
import { AutomationRepository } from "@/repositories/automation";
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog";
import { Tooltip } from "@/components/Common/Tooltip";
import { Switch } from "@/components/Base/Switch";
import { cn } from "@/lib/utils";
import { useDynamicOptions } from "@/hooks/useDynamicOptions";
import { useAutomationBuilderAnalytics } from "@/hooks/useAutomationBuilderAnalytics";
import {
  buildAutomationExport,
  downloadAutomationExport,
} from "@/lib/automationExport";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import {
  getScheduleTrigger,
  isScheduleValueComplete,
  parseScheduleTriggerValue,
} from "@/lib/automationSchedule";

interface AutomationItemProps {
  shopId: string;
  automation: Automation;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}
export const AutomationItem = ({
  shopId,
  automation,
  canMoveUp,
  canMoveDown,
}: AutomationItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localState, setLocalState] = useState(automation);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const { generateOptions } = useDynamicOptions();
  const { logExportUsed } = useAutomationBuilderAnalytics();

  // The body renders `localState` while editing and the live prop otherwise.
  // Rendering `localState` unconditionally would be wrong in the other
  // direction: it is seeded once at mount, so a reorder or another session's
  // edit arriving over the subscription would never show.
  const displayed = isEditing ? localState : automation;

  const handleEditClick = () => {
    if (isEditing) {
      void handleSave();
      return;
    }
    // Re-seed from the live prop on every entry into edit mode, so a reorder
    // or another session's edit that arrived over the Firestore subscription
    // while this row was read-only is reflected at the start of the new
    // edit session. handleSave now writes a field patch (not the whole
    // object), so a moveUp/moveDown on another row while this row's edit is
    // still open can no longer be clobbered on save (WEBV3-193) — this
    // re-seed is purely about starting from a fresh snapshot, not about
    // narrowing a write-shape race.
    setLocalState(automation);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const scheduleTrigger = getScheduleTrigger(localState);
    if (
      scheduleTrigger &&
      !isScheduleValueComplete(
        parseScheduleTriggerValue(scheduleTrigger.triggerValue)
      )
    ) {
      showErrorToast(
        'An "On a schedule" trigger set to specific days needs at least one day selected, and a valid time.'
      );
      return;
    }
    setIsSaving(true);
    try {
      // Field patch, not the whole object — AutomationRepository.update
      // performs a Firestore merge-patch write, so only the fields named
      // here are ever touched. `sortOrder` and `enabled` are deliberately
      // excluded: this component has no edit affordance for either, and
      // omitting `sortOrder` is what stops a stale edit-buffer value from
      // ever clobbering a concurrent reorder on another row (WEBV3-193).
      // `name`/`description` are optional on Automation and must be omitted
      // rather than sent as `undefined` — Firestore's updateDoc throws on an
      // explicit undefined field value.
      await AutomationRepository.update(shopId, automation.id, {
        triggers: localState.triggers,
        actions: localState.actions,
        triggersConditional: localState.triggersConditional,
        ...(localState.name !== undefined && { name: localState.name }),
        ...(localState.description !== undefined && {
          description: localState.description,
        }),
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      // Stay in edit mode on a failed save — the body renders the live
      // `automation` prop once we're not editing, so exiting here would make
      // the user's edits appear to silently revert (indistinguishable from a
      // successful save) with no way to recover them.
      showErrorToast(
        "Couldn't save the automation. Your changes are still here — try again."
      );
    }
    setIsSaving(false);
  };

  const handleEdit = (newAutomation: Automation) => {
    setLocalState(newAutomation);
  };

  const handleDelete = async () => {
    setIsEditing(false);
    setIsSaving(true);
    try {
      await AutomationRepository.delete(shopId, automation.id);
    } catch (error) {
      console.error(error);
    }
    setIsSaving(false);
  };

  const handleMoveUp = async () => {
    setIsSaving(true);
    try {
      await AutomationRepository.moveUp(shopId, automation.id);
    } catch (error) {
      console.error(error);
    }
    setIsSaving(false);
  };

  const handleMoveDown = async () => {
    setIsSaving(true);
    try {
      await AutomationRepository.moveDown(shopId, automation.id);
    } catch (error) {
      console.error(error);
    }
    setIsSaving(false);
  };

  const handleToggleEnabled = async () => {
    setIsSaving(true);
    try {
      await AutomationRepository.update(shopId, automation.id, {
        enabled: !automation.enabled,
      });
    } catch (error) {
      console.error(error);
      showErrorToast("Couldn't update the automation. Try again.");
    }
    setIsSaving(false);
  };

  const handleExport = () => {
    try {
      const exportFile = buildAutomationExport(
        [automation],
        shopId,
        generateOptions
      );
      downloadAutomationExport(
        exportFile,
        `automation-${automation.id}-${Date.now()}.json`
      );
      showSuccessToast("Automation exported successfully");
      logExportUsed("single");
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to export automation");
    }
  };

  return (
    <div
      className={cn(
        "grid grid-cols-12 items-start justify-between gap-4",
        !automation.enabled && "opacity-60"
      )}
    >
      <ConfirmDialog
        open={confirmDeleteOpen}
        onConfirm={handleDelete}
        setOpen={setConfirmDeleteOpen}
        message={`You are about to delete the automation "${displayed.name}". This action cannot be undone. Are you sure you want to continue?`}
      />
      <div className="col-span-2 flex flex-col items-start gap-2">
        <Label>Name</Label>
        <Input
          className="text-primary"
          value={displayed.name}
          onChange={(e) =>
            setLocalState({ ...localState, name: e.target.value })
          }
          disabled={!isEditing}
        />
      </div>
      <div className="col-span-8 flex flex-col items-start gap-2">
        <Label>Automation</Label>
        <AutomationDisplay
          automation={displayed}
          isEditing={isEditing}
          onEdit={handleEdit}
        />
      </div>
      <div className="col-span-2 flex flex-col items-start gap-2">
        <Label>Actions</Label>
        <div className="flex flex-row flex-wrap items-center justify-end gap-2">
          <Switch
            // Legacy documents created before `enabled` existed on the schema
            // have no value for it at all. `checked={undefined}` would make
            // Radix's Switch run uncontrolled, letting its internal state
            // diverge from the document (e.g. staying visually "on" after a
            // failed write). Coalescing to `false` keeps it controlled and
            // matches the dim class's own `!automation.enabled` treatment of
            // a missing value as disabled.
            checked={automation.enabled ?? false}
            onCheckedChange={handleToggleEnabled}
            disabled={isSaving || isEditing}
            aria-label={
              automation.enabled ? "Disable automation" : "Enable automation"
            }
          />
          <Tooltip message="Export as JSON">
            <Button
              variant={"ghost-alt"}
              onClick={handleExport}
              size={"icon"}
              disabled={isSaving || isEditing}
            >
              <DownloadIcon />
            </Button>
          </Tooltip>
          <Button
            variant={"ghost-alt"}
            onClick={handleMoveDown}
            size={"icon"}
            disabled={isSaving || isEditing || !canMoveDown}
          >
            <ArrowDownIcon />
          </Button>
          <Button
            variant={"ghost-alt"}
            onClick={handleMoveUp}
            size={"icon"}
            disabled={isSaving || isEditing || !canMoveUp}
          >
            <ArrowUpIcon />
          </Button>
          <Button
            variant={isEditing ? "accent" : "ghost"}
            onClick={handleEditClick}
            size={"icon"}
            disabled={isSaving}
            aria-label={isEditing ? "Save automation" : "Edit automation"}
          >
            {isEditing ? <SaveIcon /> : <PencilIcon />}
          </Button>
          <Button
            variant="destructive"
            onClick={() => setConfirmDeleteOpen(true)}
            size={"icon"}
            disabled={isSaving}
          >
            <TrashIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};
